import type { UserAsset } from '@/manage/types/user'

export type MembershipVipBadgeTone =
  | 'trial'
  | 'starter'
  | 'basic'
  | 'pro-monthly'
  | 'pro-annual'
  | 'professional'
  | 'pro'
  | 'enterprise'
  | 'business'

export interface MembershipVipBadge {
  label: 'VIP' | 'PRO' | 'Enterprise' | 'MAX' | 'BUSINESS'
  tone: MembershipVipBadgeTone
}

const normalizeMembershipText = (value: unknown) => String(value || '').trim().toLowerCase()

const includesAny = (value: string, keywords: string[]) =>
  keywords.some((keyword) => value.includes(keyword))

const isAnnualCycle = (value: string) =>
  includesAny(value, [
    'annual',
    'yearly',
    'year',
    '12_month',
    '12month',
    '12-month',
    '年付',
    '年费',
    '包年',
    '年度',
  ])

const isEnterprisePlan = (value: string) =>
  includesAny(value, [
    'enterprise',
    'business',
    'commercial',
    '企业',
    '商业',
    '商务',
  ])

const isTrialMembership = (identity: string, statuses: string[]) =>
  statuses.includes('trialing') ||
  includesAny(identity, ['trial', '试用', '体验'])

const isStarterPlan = (value: string) =>
  includesAny(value, ['starter', 'basic', '新手', '入门', '基础'])

const isProfessionalPlan = (value: string) =>
  includesAny(value, ['professional', 'pro', '专业', 'team', '团队'])

export const getMembershipVipBadge = (
  asset: UserAsset | null | undefined,
): MembershipVipBadge | null => {
  if (!asset?.is_vip && !asset?.is_enterprise_mode) return null

  const subscription = asset.subscription
  const currentMembership = subscription?.current_membership
  const entitlement = asset.entitlement

  // 1. Gather all identity text
  const membershipIdentity = [
    asset.vip_level,
    asset.vip_level_name,
    currentMembership?.vip_level,
    currentMembership?.plan_key,
    currentMembership?.plan_name,
    currentMembership?.product_id,
    entitlement?.vip_level,
    entitlement?.plan_key,
    entitlement?.plan_name,
    entitlement?.product_id,
    subscription?.vip_level,
    subscription?.plan_key,
    subscription?.plan_name,
    subscription?.product_id,
  ]
    .map(normalizeMembershipText)
    .filter(Boolean)
    .join(' ')

  // 2. Gather billing cycle clues
  const billingCycleClues = [
    (currentMembership as Record<string, unknown> | undefined)?.billing_cycle,
    (currentMembership as Record<string, unknown> | undefined)?.billingCycle,
    (entitlement as Record<string, unknown> | undefined)?.billing_cycle,
    (entitlement as Record<string, unknown> | undefined)?.billingCycle,
    (subscription as Record<string, unknown> | undefined)?.billing_cycle,
    (subscription as Record<string, unknown> | undefined)?.billingCycle,
    (asset as unknown as Record<string, unknown> | undefined)?.billing_cycle,
    (asset as unknown as Record<string, unknown> | undefined)?.billingCycle,
    currentMembership?.plan_key,
    currentMembership?.plan_name,
    entitlement?.plan_key,
    entitlement?.plan_name,
    subscription?.plan_key,
    subscription?.plan_name,
    asset.vip_level_name,
  ]
    .map(normalizeMembershipText)
    .filter(Boolean)
    .join(' ')

  const membershipStatuses = [
    currentMembership?.status,
    entitlement?.status,
    subscription?.status,
  ].map(normalizeMembershipText)

  // 1. 企业版会员一律显示 Enterprise 金黄色
  if (asset.is_enterprise_mode || asset.vip_level === 'enterprise' || isEnterprisePlan(membershipIdentity)) {
    return { label: 'Enterprise', tone: 'enterprise' }
  }

  // 2. 试用会员显示 VIP 淡蓝色
  if (asset.vip_level === 'trial' || isTrialMembership(membershipIdentity, membershipStatuses)) {
    return { label: 'VIP', tone: 'trial' }
  }

  // 3. 专业版会员：年费显示 PRO 深蓝色，月付显示 PRO 淡蓝色
  if (
    asset.vip_level === 'professional' ||
    asset.vip_level === 'professional_pro' ||
    isProfessionalPlan(membershipIdentity)
  ) {
    if (isAnnualCycle(billingCycleClues)) {
      return { label: 'PRO', tone: 'pro-annual' }
    }
    return { label: 'PRO', tone: 'pro-monthly' }
  }

  // 4. 新手会员显示 VIP 深蓝色
  if (asset.vip_level === 'basic' || isStarterPlan(membershipIdentity)) {
    return { label: 'VIP', tone: 'starter' }
  }

  // 默认兜底
  return { label: 'VIP', tone: 'starter' }
}
